// =====================================================
// CONSULTANT REGISTRATION CONSTANTS
// =====================================================

export const ALL_LANGUAGES = [
  'French',
  'English',
  'Spanish',
  'German',
  'Italian',
  'Portuguese',
  'Russian',
  'Mandarin',
  'Arabic',
  'Japanese',
  'Other',
] as const;

export const INSTITUTION_TYPES = [
  // Independent Roles
  {
    category: 'independent_roles',
    values: [
      'individual_self_employed',
      'virtual_assistant',
      'researcher_consultant',
      'coach_planner',
    ],
  },
  // Business Services
  {
    category: 'business_services',
    values: [
      'financial_firm',
      'accounting_tax_firm',
      'law_firm',
      'marketing_pr_agency',
      'it_software_firm',
      'human_resources',
      'media_agency',
      'business_intelligence',
      'property_management',
    ],
  },
  // Specific Industries
  {
    category: 'specific_industries',
    values: [
      'construction_trades',
      'residential_services',
      'manufacturing_operations',
      'transportation_delivery',
      'waste_management',
      'agriculture_consulting',
      'retail_ecommerce',
      'hospitality_tourism',
      'healthcare_services',
      'automotive_industry',
      'aviation_marine',
      'sports_management',
      'interior_design',
      'other_specialized',
    ],
  },
] as const;

export const INDUSTRIES = [
  { value: 'agriculture_food', label: 'AGRICULTURE, AGRI-FOOD & FOOD INDUSTRY' },
  { value: 'automotive_marine', label: 'AUTOMOTIVE & BOAT / MARINE' },
  { value: 'beauty_personal', label: 'BEAUTY & PERSONAL CARE' },
  { value: 'retail', label: 'RETAIL (General, Sports, Specialized)' },
  { value: 'communication_media', label: 'COMMUNICATION, MEDIA & PUBLIC RELATIONS' },
  { value: 'engineering_consulting', label: 'ENGINEERING & TECHNICAL CONSULTING (Civil, Electrical, Chemical, Aero)' },
  { value: 'construction', label: 'BUILDING & CONSTRUCTION (incl. Trades & HVAC)' },
  { value: 'entertainment_sport', label: 'ENTERTAINMENT, RECREATION & SPORT' },
  { value: 'education', label: 'EDUCATION & CHILDREN SERVICES' },
  { value: 'tech_saas', label: 'ONLINE & TECHNOLOGY (SaaS, Web Dev, IT Services)' },
  { value: 'service_general', label: 'SERVICE BUSINESSES (General)' },
  { value: 'manufacturing', label: 'MANUFACTURING & INDUSTRIAL PRODUCTION' },
  { value: 'accounting_tax', label: 'ACCOUNTING & TAX FIRM (CPA, CA)' },
  { value: 'legal_firm', label: 'LEGAL FIRM (Corporate, IP, Real Estate)' },
  { value: 'waste_environmental', label: 'WASTE MANAGEMENT, WATER TREATMENT & ENVIRONMENTAL' },
  { value: 'wholesale', label: 'WHOLESALE & DISTRIBUTORS' },
  { value: 'business_intelligence', label: 'BUSINESS INTELLIGENCE & DATA CONSULTING' },
  { value: 'non_classifiable', label: 'NON-CLASSIFIABLE / OTHER' },
  { value: 'recruitment_hr', label: 'RECRUITMENT & HUMAN RESOURCES' },
  { value: 'restaurant_food', label: 'RESTAURANTS & FOOD SERVICE' },
  { value: 'home_services', label: 'HOME SERVICES (Residential & Commercial Contractors)' },
  { value: 'financial_insurance', label: 'FINANCIAL SERVICES & INSURANCE' },
  { value: 'real_estate', label: 'REAL ESTATE SERVICES & PROPERTY MANAGEMENT' },
  { value: 'pet_services', label: 'PET SERVICES' },
  { value: 'healthcare', label: 'HEALTH CARE, MEDICAL & LIFE SCIENCES' },
  { value: 'transportation', label: 'TRANSPORTATION, TRUCKING & LOGISTICS' },
  { value: 'travel_tourism', label: 'TRAVEL, TOURISM & HOSPITALITY' },
] as const;

// Common expertise options for ALL sectors
export const COMMON_EXPERTISE_OPTIONS = [
  'business_owner',
  'worker_in_sector',
  'licenses_permits',
] as const;

// Specific expertise groups by theme
export const EXPERTISE_GROUPS = {
  FINANCIAL_SERVICES: [
    'due_diligence_financial',
    'due_diligence_technical',
    'business_valuation',
    'transaction_negotiation',
    'acquisition_tax_planning',
    'financial_modeling',
    'working_capital_optimization',
    'banking_compliance',
    'wealth_management',
    'credit_risk_analysis',
    'brokerage_investments',
  ],
  LEGAL_SERVICES: [
    'due_diligence_legal',
    'corporate_law',
    'real_estate_law',
    'ma_law',
    'labor_law',
    'business_litigation',
    'ip_law',
    'tax_law',
    'immigration_law',
  ],
  ACCOUNTING_TAX: [
    'audit_certification',
    'tax_planning',
    'tax_compliance',
    'compliance_audit',
    'management_accounting',
    'internal_control',
  ],
  ENGINEERING_DESIGN: [
    'civil_engineering',
    'architecture',
    'mechanical_engineering',
    'industrial_engineering',
    'energy_engineering',
    'chemical_engineering',
    'electrical_engineering',
    'aerospace_engineering',
    'interior_design',
  ],
  BUILDING_TRADES: [
    'building_code_compliance',
    'construction_project_management',
    'workforce_management',
    'quality_control',
    'estimation_bidding',
    'site_coordination',
  ],
  IT_TECH_DATA: [
    'cybersecurity_compliance',
    'software_engineering',
    'software_architecture',
    'software_patents',
    'business_intelligence',
    'it_automation',
    'infrastructure_networks',
    'technical_support',
  ],
  MEDICAL_LIFESCIENCES: [
    'biomedical_engineering',
    'clinical_research',
    'biological_data_analysis',
    'health_compliance',
    'pharmacovigilance',
    'equipment_operations',
    'packaging_line_management',
    'medical_marketing',
    'practice_management',
    'nursing_care',
  ],
  OPERATIONS_LOGISTICS: [
    'due_diligence_operational',
    'post_merger_integration',
    'lean_six_sigma',
    'supply_chain_management',
    'international_transportation',
    'specialized_logistics',
    'industrial_automation',
    'inventory_management',
    'production_optimization',
    'warehouse_management',
  ],
  ENVIRONMENTAL: [
    'waste_water_management',
    'environmental_compliance',
    'impact_assessment',
    'sustainability_consulting',
    'site_remediation',
  ],
  MARKETING_MEDIA: [
    'customer_acquisition',
    'marketing_roi_analysis',
    'public_relations',
    'brand_strategy',
    'advertising_campaigns',
    'content_marketing',
    'audiovisual_production',
    'media_distribution',
    'social_media_management',
    'graphic_design',
    'advertising_design',
  ],
  SALES_COMMERCE: [
    'sales_digital_transformation',
    'business_development',
    'key_account_management',
    'ecommerce_platforms',
    'omnichannel_strategies',
    'conversion_optimization',
  ],
  AGRI_FOOD: [
    'agricultural_consulting',
    'biotech_rd',
    'agricultural_development',
    'food_regulation',
    'farm_management',
    'food_processing',
  ],
  HR_SERVICES: [
    'recruitment_staffing',
    'compensation_benefits',
    'training_development',
    'talent_management',
    'labor_relations',
    'occupational_health',
  ],
  REAL_ESTATE: [
    'real_estate_appraisal',
    'property_management',
    'commercial_brokerage',
    'real_estate_development',
    'leasing_administration',
  ],
  EDUCATION: [
    'curriculum_development',
    'professional_education',
    'specialized_education',
    'school_administration',
    'tutoring_support',
  ],
  AUTOMOTIVE_MARINE: [
    'automotive_repair',
    'bodywork_painting',
    'vehicle_sales',
    'marine_maintenance',
    'marina_services',
  ],
  BEAUTY_PERSONAL: [
    'hairdressing_aesthetics',
    'spa_wellness',
    'salon_management',
    'beauty_training',
    'beauty_products_sales',
  ],
  PET_SERVICES: [
    'veterinary_care',
    'grooming',
    'daycare_boarding',
    'training_behavior',
    'mobile_pet_services',
  ],
  RESTAURANT_FOOD: [
    'restaurant_management',
    'chef_cooking',
    'service_experience',
    'procurement_control',
    'menu_development',
  ],
  RETAIL: [
    'store_management',
    'visual_merchandising',
    'pos_management',
    'procurement_purchasing',
    'customer_service',
  ],
  HOSPITALITY_TOURISM: [
    'hotel_management',
    'concierge_services',
    'event_planning',
    'travel_agency',
    'tourist_attractions',
  ],
  ENTERTAINMENT_SPORT: [
    'sports_event_management',
    'coaching_training',
    'facility_management',
    'event_production',
    'entertainment_shows',
  ],
  HOME_SERVICES: [
    'plumbing_heating',
    'electrical_systems',
    'hvac_ventilation',
    'roofing_siding',
    'window_installation',
    'window_cleaning',
    'landscaping_pool',
    'painting_finishes',
    'handyman_repairs',
    'cleaning_maintenance',
  ],
  WHOLESALE_DISTRIBUTION: [
    'distribution_management',
    'supplier_relations',
    'distribution_logistics',
    'b2b_wholesale',
  ],
  OTHER_SERVICES: [
    'translation',
    'interpretation',
    'writing',
    'virtual_assistant',
    'web_research',
    'database_creation',
  ],
} as const;

// Industry to expertise groups mapping
export const INDUSTRY_EXPERTISE_MAPPING: Record<string, (keyof typeof EXPERTISE_GROUPS)[]> = {
  agriculture_food: ['AGRI_FOOD', 'OPERATIONS_LOGISTICS'],
  automotive_marine: ['AUTOMOTIVE_MARINE', 'OPERATIONS_LOGISTICS'],
  beauty_personal: ['BEAUTY_PERSONAL', 'SALES_COMMERCE'],
  construction: ['BUILDING_TRADES', 'ENGINEERING_DESIGN'],
  communication_media: ['MARKETING_MEDIA'],
  education: ['EDUCATION', 'HR_SERVICES'],
  entertainment_sport: ['ENTERTAINMENT_SPORT', 'SALES_COMMERCE'],
  financial_insurance: ['FINANCIAL_SERVICES', 'ACCOUNTING_TAX'],
  healthcare: ['MEDICAL_LIFESCIENCES', 'IT_TECH_DATA'],
  home_services: ['HOME_SERVICES'],
  manufacturing: ['OPERATIONS_LOGISTICS', 'ENGINEERING_DESIGN'],
  tech_saas: ['IT_TECH_DATA', 'SALES_COMMERCE'],
  pet_services: ['PET_SERVICES'],
  restaurant_food: ['RESTAURANT_FOOD', 'OPERATIONS_LOGISTICS'],
  retail: ['RETAIL', 'SALES_COMMERCE'],
  service_general: ['OPERATIONS_LOGISTICS', 'SALES_COMMERCE'],
  transportation: ['OPERATIONS_LOGISTICS'],
  travel_tourism: ['HOSPITALITY_TOURISM', 'SALES_COMMERCE'],
  wholesale: ['WHOLESALE_DISTRIBUTION', 'OPERATIONS_LOGISTICS'],
  accounting_tax: ['ACCOUNTING_TAX', 'FINANCIAL_SERVICES'],
  legal_firm: ['LEGAL_SERVICES'],
  engineering_consulting: ['ENGINEERING_DESIGN', 'IT_TECH_DATA'],
  real_estate: ['REAL_ESTATE'],
  waste_environmental: ['ENVIRONMENTAL', 'OPERATIONS_LOGISTICS'],
  business_intelligence: ['IT_TECH_DATA'],
  recruitment_hr: ['HR_SERVICES'],
  non_classifiable: ['OTHER_SERVICES'],
};

export const EXPERIENCE_YEARS_OPTIONS = [
  { value: '0-2', label: '0 to 2 years' },
  { value: '3-5', label: '3 to 5 years' },
  { value: '6-10', label: '6 to 10 years' },
  { value: '10+', label: '10 years and more' },
] as const;

export const CANADIAN_PROVINCES = [
  'ON', 'QC', 'AB', 'BC', 'MB', 'SK', 'NS', 'NB', 'PE', 'NL', 'NT', 'YT', 'NU'
] as const;

export const USA_STATES = [
  'CA', 'NY', 'FL', 'TX', 'IL', 'PA', 'OH', 'MI', 'NC'
] as const;

export const EUROPE_COUNTRIES = [
  'France', 'United Kingdom', 'Germany', 'Switzerland', 'Luxembourg', 'Belgium'
] as const;

export const COMPENSATION_OPTIONS = [
  { value: 'per_hour', label: 'Hourly rate (specify below)' },
  { value: 'per_project', label: 'Per project fee (specify below)' },
  { value: 'equity_partnership', label: 'Partnership for equity stake in company to be acquired' },
] as const;

export const CURRENCIES = ['CAD', 'USD', 'EURO', 'BTC'] as const;

export const AVAILABILITY_OPTIONS = [
  { value: 'less_10h', label: 'Less than 10 hours/week' },
  { value: '10_20h', label: '10 to 20 hours/week' },
  { value: 'more_20h', label: 'More than 20 hours/week (part-time)' },
  { value: 'full_time', label: 'Full-time (exclusive or near-exclusive)' },
  { value: 'to_be_agreed', label: 'To be agreed / on demand (depending on transaction)' },
] as const;

export const HOW_HEARD_OPTIONS = {
  en: [
    'Facebook',
    'LinkedIn',
    'Instagram',
    'Networking Group',
    'YouTube',
    'Google Search',
    'Friends',
    'Family',
    'Other',
  ],
  fr: [
    'Facebook',
    'LinkedIn',
    'Instagram',
    'Groupe de réseautage',
    'Youtube',
    'Recherches Google',
    'Amis',
    'Famille',
    'Autre',
  ],
} as const;

export const TOTAL_STEPS = 4;

export const STEP_NAMES = ['Account', 'Profile', 'Services', 'Compliance'] as const;
